import Popover from "components/Popover/Popover";
import ToolboxButton, {
  ToolboxButtonIcon,
} from "Dashboard/components/Buttons/ToolboxButton";
import { IOption } from "Dashboard/domain/IOption";
import { SearchType } from "Dashboard/domain/SearchType";
import { IConcreteBaseInputCreate } from "Dashboard/scenes/ConcreteBase/domain/IConcreteBaseInputCreate";
import { IGetConcreteBaseInputsRequestParams } from "Dashboard/scenes/ConcreteBase/domain/IGetConcreteBaseInputsRequestParams";
import classesToolbox from "Dashboard/scenes/ConcreteBase/styles/toolbox.module.scss";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Control,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue,
  useForm
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NewConcreteBaseOutputPopover from "../../Output/components/NewConcreteBaseOutputPopover";
import {
  selectState,
  setItemInventoryPopover,
  setNewInputPopover,
  setNewOutputPopover,
  setOwnProductPopover,
  setSearchInputPopover
} from "../inputSlice";
import ItemInventoryPopover from "./ItemInventorytPopover";
import NewInputPopover from "./NewInputPopover";
import OwnProductionPopover from "./OwnProductionPopover";
import PrintStateOfInput from "./PrintStateOfInput";
import SearchPopover from "./SearchPopover";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasWriteAccessTo } from "utils/authUtil";
import { useSelector } from "react-redux";
import { CONCRETE_BASE_MODULE_NAME } from "Dashboard/scenes/ConcreteBase/domain/ConcreteBaseModule";
import { usePagePrint } from "hooks/usePagePrint";
import { IConcreteBaseOutputCreate } from "Dashboard/scenes/ConcreteBase/domain/IConcreteBaseOutputCreate";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";
import { useLocalStorage } from "hooks/LocalStorage";

interface IProps {
  defaultGetInputsRequestParams: IGetConcreteBaseInputsRequestParams;
  setGetInputsRequestParams: Dispatch<SetStateAction<IGetConcreteBaseInputsRequestParams>>;
  registerSearch: UseFormRegister<IGetConcreteBaseInputsRequestParams>;
  handleSubmitSearch: UseFormHandleSubmit<IGetConcreteBaseInputsRequestParams>;
  resetSearch: UseFormReset<IGetConcreteBaseInputsRequestParams>;
  controlSearch: Control<IGetConcreteBaseInputsRequestParams, any>;
  getValuesSearch: UseFormGetValues<IGetConcreteBaseInputsRequestParams>;
  setFocusSearch: UseFormSetFocus<IGetConcreteBaseInputsRequestParams>;
  setValueSearch: UseFormSetValue<IGetConcreteBaseInputsRequestParams>;
};

const Toolbox = ({
  defaultGetInputsRequestParams,
  setGetInputsRequestParams,
  registerSearch,
  handleSubmitSearch,
  resetSearch,
  controlSearch,
  getValuesSearch,
  setFocusSearch,
  setValueSearch,
}: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = usePagePrint(
    componentRef, { margins: "0.5cm" });

  const {
    outputValues,
    newInputPopover,
    searchInputPopover,
    ownProductPopover,
    newOutputPopover,
    itemInventoryPopover } = useAppSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const {
    register: registerNewInput,
    setValue: setValueNewInput,
    getValues: getValuesNewInput,
    handleSubmit: handleSubmitNewInput,
    control: controlNewInput,
    formState: { errors: errorsNewInput },
    reset: resetNewInput,
  } = useForm<IConcreteBaseInputCreate>();
  const [knownItemCodes, setKnownItemCodes] = useState<string[]>([]);
  const [supplierValue, setSupplierValue]
    = useState<IOption | undefined>(undefined);
  const [driverValue, setDriverValue]
    = useState<IOption | undefined>(undefined);

  const [searchTypeValue, setSearchTypeValue] = useState(SearchType.BASIC);
  const [storedSearchTerm, setStoredSearchTerm]
    = useLocalStorage("cbSearchTerm", "");
  const [storedSearchType, setStoredSearchType]
    = useLocalStorage("cbSearchType", SearchType.BASIC);
  const [storedSearchInputs, setStoredSearchInputs]
    = useLocalStorage("cbSearchInputs", null);

  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch());

  useEffect(() => {
    if (storedSearchType === "BASIC") {
      setGetInputsRequestParams({ searchTerm: storedSearchTerm });
    }
    if (storedSearchType === "ADVANCED") {
      storedSearchInputs.searchTerm = undefined;
      setGetInputsRequestParams({ ...storedSearchInputs });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  useEffect(() => {
    if (storedSearchTerm) {
      setValueSearch("searchTerm", storedSearchTerm);
    }
    if (storedSearchType) {
      setSearchTypeValue(storedSearchType);
    }
    if (storedSearchInputs) {
      Object.keys(storedSearchInputs).forEach((key: any) => {
        if (key !== "searchTerm") {
          setValueSearch(key, storedSearchInputs[key]);
        }
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  const handleNavigation = (path: string) => {
    if (path === '/dashboard') {
      localStorage.removeItem("cbSearchTerm");
      localStorage.removeItem("cbSearchType");
      localStorage.removeItem("cbSearchInputs");
      localStorage.removeItem("cbOutputSearchQuery");
      localStorage.removeItem("cbOutputSearchType");
      localStorage.removeItem("cbOutputSearchInputs");
      resetSearch(defaultGetInputsRequestParams);
    }
    else {
      const currentSearchTerm = getValuesSearch("searchTerm");
      const currentSearchInputs = getValuesSearch();
      setStoredSearchTerm(currentSearchTerm);
      setStoredSearchType(searchTypeValue);
      setStoredSearchInputs(currentSearchInputs);
    }
    navigate(path);
  };

  const {
    register: registerOutputProjectOrPartner,
    handleSubmit: handleSubmitOutputProjectOrPartner,
    control: controlOutputProjectOrPartner,
    getValues: getValuesOutputProjectOrPartner,
    setValue: setValueOutputProjectOrPartner,
    formState: { errors: errorsOutputProjectOrPartner },
    reset: resetOutputProjectOrPartner,
  } = useForm<IConcreteBaseOutputCreate>();
  const [driverOutputValue, setDriverOutputValue] = useState<IOption | undefined>(undefined);
  const [vehicleValue, setVehicleValue] = useState<IOption | undefined>(undefined);
  const [partnerValue, setPartnerValue] = useState<IOption | undefined>(undefined);
  const [outputType, setOutputType] = useState("Project");

  const handleOwnProduct = () => {
    if (outputValues.repository !== undefined
      && outputValues.repository.shortName !== 'RMbb') {
      toast.warning(`Nije moguća proizvodnja od artikala iz magacina ${outputValues.repository.name}`);
    } else {
      dispatch(setOwnProductPopover({ isOpen: true }));
    }
  };

  const handleNewOutput = () => {
    if (outputValues.repository === undefined) {
      toast.warning("Morate prvo izabrati artikle za izdavanje");
    } else if (outputValues.repository.shortName === 'RMbb') {
      toast.warning(`Nije moguće izdavanje artikala iz magacina ${outputValues.repository.name}`);
    } else {
      dispatch(setNewOutputPopover({ isOpen: true }));
    }
  };

  const handleReset = () => {
    resetSearch(defaultGetInputsRequestParams);
    setGetInputsRequestParams(defaultGetInputsRequestParams);
  };

  return (
    <>
      <nav className="container nav-arrows">
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Back}
            tooltip="Glavni meni"
            toolbox={true}
            onClick={() => handleNavigation("/dashboard")}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Forward}
            tooltip="Betonska Baza - Izlaz"
            toolbox={true}
            onClick={() => handleNavigation("/betonska-baza/output")}
          />
        </div>
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Add}
            tooltip="Novi ulaz"
            toolbox={true}
            onClick={hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
              ? () => dispatch(setNewInputPopover({
                isOpen: true
              })) : undefined}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Search}
            tooltip="Traži"
            toolbox={true}
            onClick={() => dispatch(
              setSearchInputPopover({ isOpen: true }))}
          />
          {!isSearchFormEmpty
            && (<ToolboxButton
              icon={ToolboxButtonIcon.CancelSearch}
              tiny={true}
              tooltip="Pokaži sve"
              onClick={() => handleReset()}
            />)}
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampa"
            toolbox={true}
            onClick={handlePrint}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Concrete}
            tooltip="Proizvodnja i izdavanje sopstvenih proizvoda"
            toolbox={true}
            onClick={hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
              ? handleOwnProduct : undefined}
          />
          <div className="cb-input-icon-container">
            <ToolboxButton
              icon={ToolboxButtonIcon.RemoveCircle}
              tooltip="Izdavanje betona i galanterije"
              toolbox={true}
              onClick={hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
                ? handleNewOutput : undefined}
            />
          </div>
        </div>
        <div ref={componentRef} className="print-area">
          <PrintStateOfInput />
        </div>
        <div className={classesToolbox.right}>
          <ToolboxButton
            icon={ToolboxButtonIcon.Hamburger}
            tooltip="Šifarnik"
            toolbox={true}
            onClick={(event) => dispatch(setItemInventoryPopover({
              isOpen: true, top: event.clientY, left: event.clientX
            }))}
          />
        </div>
      </nav>
      <Popover
        isOpen={newInputPopover.isOpen}
        onClose={() => dispatch(setNewInputPopover({ isOpen: false }))}
        title="Novi"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 250 }}
      >
        <NewInputPopover
          handleSubmit={handleSubmitNewInput}
          setValue={setValueNewInput}
          register={registerNewInput}
          control={controlNewInput}
          getValues={getValuesNewInput}
          errors={errorsNewInput}
          reset={resetNewInput}
          knownItemCodes={knownItemCodes}
          setKnownItemCodes={setKnownItemCodes}
          supplierValue={supplierValue}
          setSupplierValue={setSupplierValue}
          driverValue={driverValue}
          setDriverValue={setDriverValue}
        />
      </Popover>
      <Popover
        isOpen={searchInputPopover.isOpen}
        onClose={() => {
          dispatch(setSearchInputPopover({ isOpen: false }));
        }}
        title="Pretraga"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 315 }}
      >
        <SearchPopover
          register={registerSearch}
          control={controlSearch}
          handleSubmit={handleSubmitSearch}
          setFocus={setFocusSearch}
          searchType={searchTypeValue}
          setSearchType={setSearchTypeValue}
          getValues={getValuesSearch}
          setValue={setValueSearch}
          reset={resetSearch}
          setGetInputsRequestParams={setGetInputsRequestParams}
        />
      </Popover>
      <Popover
        isOpen={ownProductPopover.isOpen}
        onClose={() => dispatch(
          setOwnProductPopover({ isOpen: false }))}
        title="Proizvodnja sopstvenog proizvoda"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 440 }}
      >
        <OwnProductionPopover />
      </Popover>
      <Popover
        isOpen={newOutputPopover.isOpen}
        onClose={() => dispatch(
          setNewOutputPopover({ isOpen: false }))}
        title="Izdavanje lagera"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 570 }}
      >
        <NewConcreteBaseOutputPopover
          handleSubmit={handleSubmitOutputProjectOrPartner}
          register={registerOutputProjectOrPartner}
          control={controlOutputProjectOrPartner}
          getValues={getValuesOutputProjectOrPartner}
          setValue={setValueOutputProjectOrPartner}
          errors={errorsOutputProjectOrPartner}
          reset={resetOutputProjectOrPartner}
          driverValue={driverOutputValue}
          setDriverValue={setDriverOutputValue}
          vehicleValue={vehicleValue}
          setVehicleValue={setVehicleValue}
          type={outputType}
          setType={setOutputType}
          partnerValue={partnerValue}
          setPartnerValue={setPartnerValue}
        />
      </Popover>
      <Popover
        isOpen={itemInventoryPopover.isOpen}
        onClose={() => dispatch(
          setItemInventoryPopover({ isOpen: false }))}
        title="Šifarnik"
        direction="left"
        arrowStyle={{ top: "42px" }}
        position={{
          top: itemInventoryPopover.top,
          left: itemInventoryPopover.left,
          offsetLeft: -530,
          offsetTop: -50
        }}
      >
        <ItemInventoryPopover />
      </Popover>
    </>
  );
};

export default Toolbox;
